import { useEffect, useState } from 'react';

import {
  createDefaultDataTableLayout,
  type DataTableColumn,
  type DataTableLayout,
} from '@/UI/DataTable/types';

export function reconcileDataTableLayout<T>(
  layout: DataTableLayout,
  columns: DataTableColumn<T>[],
): DataTableLayout {
  const normalizedOrder = layout.order ?? [];
  const normalizedHidden = layout.hidden ?? [];
  const normalizedWidths = layout.widths ?? {};
  const available = columns.filter((column) => column.isAvailable !== false);
  const ids = new Set(available.map(({ id }) => id));
  const known = new Set(normalizedOrder);
  const added = available.filter(({ id }) => !known.has(id));
  const order = [
    ...normalizedOrder.filter((id) => ids.has(id)),
    ...added.map(({ id }) => id),
  ];
  const hidden = new Set(normalizedHidden.filter((id) => ids.has(id)));
  added.forEach((column) => {
    if (column.defaultHidden) hidden.add(column.id);
  });
  const widths = Object.fromEntries(
    Object.entries(normalizedWidths).filter(([id]) => ids.has(id)),
  );

  return { order, hidden: [...hidden], widths };
}

export function dataTableLayoutsEqual(
  a: DataTableLayout,
  b: DataTableLayout,
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useDataTableLayout<T>(columns: DataTableColumn<T>[]) {
  const [layout, setLayout] = useState<DataTableLayout>(() =>
    createDefaultDataTableLayout(columns),
  );

  useEffect(() => {
    setLayout((current) => {
      const next = reconcileDataTableLayout(current, columns);
      return dataTableLayoutsEqual(current, next) ? current : next;
    });
  }, [columns]);

  return [layout, setLayout] as const;
}
